import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";

import { hydrateConversationMessages, mergeByKey } from "./ChatUtils";

export function useMessagesFromAPI({
  boxRef,
  contactsRef,
  currentPhone,
  messaging,
  scrollToBottomIfNeeded,
}) {
  const [messageItems, setMessageItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [loadMoreBusy, setLoadMoreBusy] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);

  const topSentinelRef = useRef(null);
  const loadMoreMessagesRef = useRef(() => {});
  const loadMoreBusyRef = useRef(false);
  const lastEvaluatedKeyRef = useRef(null);

  const resetMessages = useCallback(() => {
    setMessageItems([]);
    setShowLoadMore(false);
    setLoading(false);
    setLastEvaluatedKey(null);
  }, []);

  const syncLatest = useCallback(
    (opts = {}) => {
      const { isNewContact } = opts;
      if (!currentPhone || !messaging) return Promise.resolve();

      if (isNewContact) {
        setLoading(true);
        setMessageItems([]);
        setShowLoadMore(false);
        setLastEvaluatedKey(null);
      }

      const threadRow = contactsRef.current.find(
        (c) => String(c.phone) === String(currentPhone)
      );
      const clearUnread =
        isNewContact && !!threadRow && Number(threadRow.unread_count) > 0;

      return messaging
        .queryMessages(currentPhone, {
          limit: 50,
          scan_index_forward: false,
          clear_unread: clearUnread,
        })
        .then((data) => {
          setLoading(false);
          const items = hydrateConversationMessages(
            (data.items || []).slice().reverse()
          );
          setLastEvaluatedKey(data.last_evaluated_key || null);
          setShowLoadMore(!!data.last_evaluated_key);
          setMessageItems(items);
          requestAnimationFrame(() => scrollToBottomIfNeeded(true));
        })
        .catch((err) => {
          setLoading(false);
          message.error(err.message || "Could not load messages");
        });
    },
    [contactsRef, currentPhone, messaging, scrollToBottomIfNeeded]
  );

  const loadMoreMessages = useCallback(() => {
    if (!currentPhone || !messaging || loadMoreBusy) return;
    if (!lastEvaluatedKey) {
      setShowLoadMore(false);
      return;
    }

    const box = boxRef.current;
    const oldHeight = box ? box.scrollHeight : 0;
    const oldTop = box ? box.scrollTop : 0;

    setLoadMoreBusy(true);
    messaging
      .queryMessages(currentPhone, {
        limit: 25,
        exclusive_start_key: lastEvaluatedKey,
        scan_index_forward: false,
      })
      .then((data) => {
        setLoadMoreBusy(false);
        const batch = (data.items || []).slice().reverse();
        const lek = data.last_evaluated_key || null;
        setLastEvaluatedKey(lek);
        setShowLoadMore(!!lek);
        if (!batch.length) {
          setShowLoadMore(false);
          return;
        }
        setMessageItems((prev) => mergeByKey(batch, prev));
        requestAnimationFrame(() => {
          const b = boxRef.current;
          if (b) b.scrollTop = b.scrollHeight - oldHeight + oldTop;
        });
      })
      .catch((err) => {
        setLoadMoreBusy(false);
        message.error(err.message || "Could not load older messages");
      });
  }, [boxRef, currentPhone, lastEvaluatedKey, loadMoreBusy, messaging]);

  loadMoreMessagesRef.current = loadMoreMessages;
  loadMoreBusyRef.current = loadMoreBusy;
  lastEvaluatedKeyRef.current = lastEvaluatedKey;

  useEffect(() => {
    if (!currentPhone) {
      resetMessages();
      return;
    }
    syncLatest({ isNewContact: true });
  }, [currentPhone, resetMessages, syncLatest]);

  useEffect(() => {
    const root = boxRef.current;
    const sentinel = topSentinelRef.current;
    if (!root || !sentinel || !currentPhone) return;
    if (!showLoadMore || !lastEvaluatedKey) return;

    let cooldown = false;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (
          cooldown ||
          loadMoreBusyRef.current ||
          !lastEvaluatedKeyRef.current
        ) {
          return;
        }
        cooldown = true;
        loadMoreMessagesRef.current();
        window.setTimeout(() => {
          cooldown = false;
        }, 650);
      },
      { root, rootMargin: "100px 0px 0px 0px", threshold: 0 }
    );

    io.observe(sentinel);
    return () => io.disconnect();
  }, [boxRef, currentPhone, lastEvaluatedKey, showLoadMore]);

  return {
    messageItems,
    setMessageItems,
    loading,
    showLoadMore,
    loadMoreBusy,
    topSentinelRef,
    loadMoreMessages,
  };
}
